import { useEffect, useState } from 'react';
import avatarIg from '@/assets/images/avatar-ig.png';
import avatarLinkedin from '@/assets/images/avatar-linkedin.png';
import useContactStore from '@/stores/ContactStore';
import useInboxStore from '@/stores/InboxStore';
import useUserStore from '@/stores/UserStore';
import { Loader2, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { trackLogOutAndMixpanelReset } from '@/lib/analytics-event';
import { removeToken } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import ChannelCard from '@/components/onboarding/ChannelCard';
import { useInstagramConnection } from '@/components/onboarding/InstagramConnection';
import { useInstagramDirectPopup } from '@/components/onboarding/InstagramDirectPopup';
import { useLinkedInPopup } from '@/components/onboarding/LinkedInPopup';

const OnboardingChannel = () => {
  const [connectionType, setConnectionType] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const nextOnboardingStage = useUserStore(
    (state) => state.nextOnboardingStage
  );
  const navigate = useNavigate();
  const { user } = useUserStore(
    useShallow((state) => ({
      user: state.user,
    }))
  );
  const resetUserStore = useUserStore((state) => state.resetUserStore);
  const resetContactStore = useContactStore((state) => state.resetContactStore);
  const resetInboxStore = useInboxStore((state) => state.resetInboxStore);

  // Custom hooks for connection logic
  const { openLinkedInPopup, loading: linkedinLoading } = useLinkedInPopup();
  const { connectInstagram, loading: instagramLoading } =
    useInstagramConnection();
  const { openInstagramDirectPopup, loading: instagramDirectLoading } =
    useInstagramDirectPopup();

  // Connection handlers
  const handleInstagramConnect = async () => {
    setConnectionType('instagram');
    await connectInstagram();
  };

  const handleLinkedInConnect = async () => {
    setConnectionType('linkedin');
    await openLinkedInPopup();
  };

  const handleInstagramDirectConnect = async () => {
    setConnectionType('instagram-direct');
    await openInstagramDirectPopup();
  };

  const handleLogout = () => {
    removeToken();

    // reset user store
    resetUserStore();
    // reset contact store
    resetContactStore();
    // reset inbox store
    resetInboxStore();

    // reset mixpanel
    trackLogOutAndMixpanelReset();
    // navigate to login page
    navigate('/login');

    // refresh the page to ensure all state is cleared
    window.location.reload();
  };

  // Check if user already has connections
  const hasInstagram = user?.customer?.ig_name;
  const hasLinkedIn = user?.customer?.linkedin_name;
  const loading = instagramLoading || linkedinLoading || instagramDirectLoading;

  useEffect(() => {
    // Wait for user data to be loaded before checking onboarding stage
    if (user) {
      setIsDataLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    // Navigate user to appropriate page based on their onboarding stage
    if (isDataLoaded) {
      if (nextOnboardingStage === 'additional_details') {
        navigate('/onboarding/setup');
      } else if (nextOnboardingStage === 'pending_selection') {
        navigate('/onboarding/instagram/select-page');
      } else if (nextOnboardingStage !== 'connect_account') {
        navigate('/inbox');
      }
    }
  }, [isDataLoaded, nextOnboardingStage, navigate]);

  // Show loading state while user data is being fetched
  if (!isDataLoaded) {
    return (
      <div className='flex h-screen items-center justify-center bg-background'>
        <div className='flex items-center gap-2'>
          <Loader2 className='h-6 w-6 animate-spin' />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className='relative flex min-h-screen items-center justify-center bg-background p-4'>
      {/* Logout Button - Top Right */}
      <div className='absolute right-4 top-4'>
        <Button
          variant='ghost'
          size='sm'
          onClick={handleLogout}
          className='flex items-center gap-2'
        >
          <LogOut className='h-4 w-4' />
          Logout
        </Button>
      </div>

      <div className='w-full max-w-4xl space-y-8'>
        {/* Header */}
        <div className='space-y-4 text-center'>
          <h1 className='text-3xl font-bold'>Connect Your Channels</h1>
          <p className='text-lg text-muted-foreground'>
            Revio works best when all your social inboxes are connected
          </p>
        </div>

        {/* Connection Options */}
        <div className='grid gap-6 md:grid-cols-2'>
          {/* Instagram Card */}
          <ChannelCard
            title='Instagram & Facebook'
            description='Connect your Instagram business account to manage messages and engage with your audience'
            avatar={avatarIg}
            isConnected={hasInstagram}
            connectedUsername={user?.customer?.ig_username}
            onConnect={handleInstagramConnect}
            loading={loading}
            connectionType='instagram'
            currentConnectionType={connectionType}
          />

          {/* LinkedIn Card */}
          <ChannelCard
            title='LinkedIn'
            description='Connect your LinkedIn account to manage professional messages and build business relationships'
            avatar={avatarLinkedin}
            isConnected={hasLinkedIn}
            connectedUsername={user?.customer?.linkedin_public_id}
            onConnect={handleLinkedInConnect}
            loading={loading}
            connectionType='linkedin'
            currentConnectionType={connectionType}
          />
        </div>

        {/* Instagram Direct Option */}
        {
          <div className='mx-auto w-fit'>
            <p className='p-2 text-lg text-muted-foreground'>
              Instead would you like to connect Instagram only?{' '}
              <button
                onClick={handleInstagramDirectConnect}
                disabled={loading}
                className='font-medium text-primary hover:underline'
              >
                Click here
              </button>
            </p>
          </div>
        }

        {/* Help Text */}
        <div className='text-center'>
          <p className='text-sm text-muted-foreground'>
            Need help? Email support -{' '}
            <a
              href='mailto:hello@getrevio.com'
              className='text-primary hover:underline'
            >
              hello@getrevio.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingChannel;
